import React from 'react';
import { Avatar } from 'primereact/avatar';
import { useHistory, useLocation } from 'react-router-dom';
import { handleDateTimeOffset } from 'utils/utility_functions/timezone';
import { timeDifference } from 'services/notification/notificationService';
import { useUserDetailsContext } from 'context/userDetailsContext';
import constants from 'constants/index';
import { generateFullName } from 'utils/generateFullNameUtils';

const NotificationContent = ({ data, markAsRead, isClient }) => {
  const { userDetails } = useUserDetailsContext();
  const history = useHistory();
  const location = useLocation();

  // List of new notification types that share common structure
  const genericNotificationTypes = [
    'create_case_team_member',
    'create_case',
    'update_settlement',
    'close_case',
    'create_lead',
    'email_invitation_to_join_lawft_expires',
    'create_payment_refund',
    'received_payment',
    'create_event',
    'update_event',
    'delete_event',
    'document_upload',
    'signature_is_received_on_a_document',
    'text_message',
    'email_is_received',
    'client_completes_a_form',
    'create_invoice',
    'event_invitation',
    'added_to_case',
    'sol_date_reminder',
  ];

  const handleNotificationClick = (e, route) => {
    if (route) {
      e.stopPropagation();
      // Only navigate if not already on the same route
      if (location.pathname !== route) {
        history.push(route);
      }
    }
  };

  const renderGenericNotification = () => {
    // Handle nested case_description structure
    const caseDescData = data?.case_description?.case_description || data?.case_description || {};
    const route = data?.body?.route || null;

    return (
      <div
        className={`${data?.is_read ? 'p-2 notification-msg' : 'p-2 unread-notification rounded-0 border-0'} ${
          route ? 'cursor-pointer' : ''
        }`}
        onClick={(e) => handleNotificationClick(e, route)}
        style={route ? { cursor: 'pointer' } : {}}
      >
        <div className="d-flex justify-content-between">
          <div className="col-12 col-sm-9">
            <div className="d-flex">
              <i className="fa fa-regular fa-bell text-muted px-3 mt-1"></i>
              <div className="ms-2">
                {data?.body?.message && (
                  <div className="mb-2">
                    <span className={data?.is_read ? '' : 'text-bold'}>{data.body.message}</span>
                  </div>
                )}
                {caseDescData?.case_name && (
                  <div className="mb-1">
                    <span className="text-muted">Case: </span>
                    <span className={data?.is_read ? '' : 'text-bold'}>{caseDescData.case_name}</span>
                  </div>
                )}
                {caseDescData?.case_number && (
                  <div className="mb-1">
                    <span className="text-muted">Case Number: </span>
                    <span>{caseDescData.case_number}</span>
                  </div>
                )}
                {data?.time && (
                  <div className="text-mutted small">
                    <span className="text-hint black-600 ms-0">{timeDifference(new Date(data?.time), new Date())} ago</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          {!data.is_read && (
            <div className=" text-end align-items-center d-flex mark-read text-nowrap">
              <a
                href="/"
                onClick={(e) => markAsRead(e, data)}
                className="flex-grow-1 text-end F-size12 text-mutted text-decoration-none ms-1"
              >
                Mark as read
              </a>
            </div>
          )}
        </div>
      </div>
    );
  };

  const notificationClientContent = () => {
    const { notification_type, toast, popup } = data || {};

    // Check if it's a generic notification type
    if (genericNotificationTypes.includes(notification_type)) {
      return renderGenericNotification();
    }

    if (notification_type === 'invitation') {
      return (
        <div className={data?.is_read ? 'p-2 notification-msg' : 'p-2 unread-notification rounded-0 border-0 '}>
          <div className="d-flex justify-content-between">
            <div className="d-flex">
              <i className="fas fa-tasks mt-1 text-muted px-3"></i>
              <div className="d-flex">
                <div className="d-flex">
                  <div className="d-flex flex-wrap">
                    {data?.body?.event_name && (
                      <div className="mx-2">
                        <span className={data.is_read ? '' : 'fw-bold'}>
                          Invitation for the event <b>{data.body.event_name}</b>{' '}
                        </span>
                      </div>
                    )}

                    {data?.body?.event_time && (
                      <div className="mx-2">
                        <span className={data.is_read ? 'text-hint black-600 ms-0' : 'text-hint black-600 ms-0 text-bold'}>
                          {handleDateTimeOffset(
                            userDetails.timezone,
                            data?.body?.event_time,
                            constants.month_date_year_time_seconds_12_format
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="d-flex justify-content-between ps-2 mt-2">
                {data?.body?.case_name && (
                  <div className="ms-4">
                    <span className={data?.is_read ? '' : 'text-bold'}>{data?.body?.case_name}</span>
                  </div>
                )}
                {data?.time && (
                  <div className="text-muted small">
                    <span className={data.is_read ? 'text-hint black-600 ms-0' : 'text-hint black-600 ms-0 text-bold'}>
                      {/* {timeDifference(new Date(data.time + 'Z'), new Date())} ago */}
                      {timeDifference(new Date(data?.time), new Date())} ago
                    </span>
                  </div>
                )}
              </div>
            </div>
            {!data.is_read && (
              <div className=" text-end align-items-center d-flex mark-read text-nowrap">
                <a
                  href="/"
                  onClick={(e) => markAsRead(e, data)}
                  className="flex-grow-1 text-end F-size12 text-mutted text-decoration-none ms-1"
                >
                  Mark as read
                </a>
              </div>
            )}
          </div>
        </div>
      );
    } else if (notification_type === 'reminder') {
      return (
        <div className={data.is_read ? 'p-2' : 'unread-notification rounded-0 border-0'}>
          <div className="d-flex justify-content-between align-items-center">
            <div className="col-12 col-sm-9 d-flex align-items-center">
              <div className="d-flex align-items-center">
                <i className="far fa-calendar-alt text-muted px-3 mt-2 align-self-start"></i>
                <div className="">
                  {data?.body && (
                    <div className="mx-2">
                      <b> {data.body.message}</b>
                    </div>
                  )}
                  {data?.body && data?.body?.event_name && (
                    <div className="mx-2">
                      <b> {data.body.event_name}</b>
                    </div>
                  )}
                  {data?.body?.due_date && (
                    <div className="mx-2">
                      <span className={data.is_read ? '' : 'text-bold'}>
                        Due Date :{' '}
                        {handleDateTimeOffset(userDetails.timezone, data.body.due_date, constants.month_date_year_time_seconds_12_format)}
                      </span>
                    </div>
                  )}
                  {data?.body && data?.body?.event_time && (
                    <div className="mx-2">
                      {handleDateTimeOffset(userDetails?.timezone, data.body.event_time, constants.month_date_year_time_seconds_12_format)}
                    </div>
                  )}

                  <div className="d-flex flex-column">
                    {data?.body?.case_name && data?.body?.case_name !== '-' && <div className="ms-2">{data?.body?.case_name}</div>}

                    {data?.time && (
                      <div className="text-mutted small mx-2">
                        <span className="text-hint black-600 ms-0">{timeDifference(new Date(data?.time), new Date())} ago</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {!data.is_read && (
              <div className=" text-end align-items-center d-flex mark-read text-nowrap">
                <a
                  href="/"
                  onClick={(e) => {
                    e.preventDefault();
                    markAsRead(e, data);
                  }}
                  className="flex-grow-1 text-end F-size12 text-mutted text-decoration-none ms-1"
                >
                  Mark as read
                </a>
              </div>
            )}
          </div>
        </div>
      );
    } else if (notification_type === 'secure_message') {
      return (
        <div className={data.is_read ? 'p-2 notification-msg' : 'p-2 unread-notification rounded-0 border-0'}>
          <div className="d-flex justify-content-between">
            <div className="col-12 col-sm-9">
              <div className="d-flex">
                {data?.body?.created_by_name?.first_name && (
                  <Avatar
                    label={data?.body?.created_by_name?.first_name?.charAt(0)}
                    className="me-3 mt-1 ms-1"
                    size="normal"
                    style={{ backgroundColor: '#9c27b0', color: '#ffffff' }}
                    shape="circle"
                  />
                )}
                <span>
                  <span className="text-bold">{generateFullName(data.body?.created_by_name)}</span>
                  <span className="fw-normal"> sent a new message</span>
                  {data?.time && (
                    <div className="text-mutted small">
                      {/* <span>{timeDifference(new Date(data?.body?.timestamp + ' UTC'), new Date())} ago</span> */}
                      <span className="text-hint black-600 ms-0">{timeDifference(new Date(data?.time), new Date())} ago</span>
                    </div>
                  )}
                </span>
              </div>
            </div>
            {!data.is_read && (
              <div className=" text-end align-items-center d-flex mark-read text-nowrap">
                <a
                  href="/"
                  onClick={(e) => markAsRead(e, data)}
                  className="flex-grow-1 text-end F-size12 text-mutted text-decoration-none ms-1"
                >
                  Mark as read
                </a>
              </div>
            )}
          </div>
        </div>
      );
    } else if (notification_type === 'assignment') {
      return (
        <div className={data?.is_read ? 'p-2 notification-msg' : 'p-2 unread-notification rounded-0 border-0'}>
          <div className="d-flex justify-content-between">
            <div className="d-flex">
              <i className="fas fa-tasks mt-1 text-muted px-3"></i>
              <div>
                <div className="d-flex flex-wrap">
                  {data?.body?.task_name && (
                    <div className="mx-2">
                      <span className={data.is_read ? '' : 'text-bold'}>
                        A task has been assigned to you: <span className="text-bold">{data.body.task_name}</span>{' '}
                      </span>
                    </div>
                  )}

                  {data?.body?.due_date && (
                    <div className="mx-2">
                      <span className={data.is_read ? '' : 'text-bold'}>
                        Due Date :{' '}
                        {handleDateTimeOffset(userDetails.timezone, data.body.due_date, constants.month_date_year_time_seconds_12_format)}
                      </span>
                    </div>
                  )}
                </div>
                {/* TODO: "-" should be handled when creating task */}
                {data?.body?.case_name && data?.body?.case_name !== '-' && (
                  <div className="ms-2">
                    <span>{data.body.case_name + ' Case'}</span>
                  </div>
                )}
              </div>
            </div>
            {!data?.is_read && (
              <div className=" text-end align-items-center d-flex mark-read text-nowrap">
                <a
                  href="/"
                  onClick={(e) => markAsRead(e, data)}
                  className="flex-grow-1 text-end F-size12 text-mutted text-decoration-none ms-1"
                >
                  Mark as read
                </a>
              </div>
            )}
          </div>
        </div>
      );
    } else if (notification_type === 'ai_summary_message') {
      return (
        <div className={data.is_read ? 'p-2 notification-msg' : 'p-2 unread-notification rounded-0 border-0'}>
          <div className="d-flex justify-content-between">
            <div className="col-12 col-sm-9 d-flex align-items-center">
              <div className="d-flex align-items-center">
                <i className="far fa-calendar-alt text-muted px-3 mt-2 align-self-start"></i>
                <div className="">
                  {data?.body && (
                    <div className="mx-2">
                      <b> {'AI File Summary'}</b>
                    </div>
                  )}
                  {data?.body && data?.body?.summary?.file_name && (
                    <div className="mx-2">
                      <b> {data.body.summary.file_name}</b>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {!data.is_read && (
              <div className=" text-end align-items-center d-flex mark-read text-nowrap">
                <a
                  href="/"
                  onClick={(e) => {
                    e.preventDefault();
                    markAsRead(e, data);
                  }}
                  className="flex-grow-1 text-end F-size12 text-mutted text-decoration-none ms-1"
                >
                  Mark as read
                </a>
              </div>
            )}
          </div>
        </div>
      );
    } else if (data?.body?.message) {
      return (
        <div className={data?.is_read ? 'p-2 border' : 'p-2 border unread-notification rounded-0 border-0'}>
          <div className="d-flex align-items-center">
            <div className="align-self-start">
              {data?.body?.contact_name || data?.body?.client_name ? (
                <span className="ms-2">
                  <Avatar
                    label={data?.body?.contact_name?.charAt(0) || data?.body?.client_name?.charAt(0)}
                    className="me-1 mt-1 "
                    size="normal"
                    style={{
                      backgroundColor: '#9c27b0 !important',
                      color: '#fff !important',
                    }}
                    shape="circle"
                  />
                </span>
              ) : (
                <i className="fa fa-regular fa-bell text-muted px-3 mt-1"></i>
              )}
            </div>

            <div className="ms-2">
              {data?.body?.message || null}
              {data?.body?.case_name && (
                <div>
                  <div>
                    Case: <span className="text-bold">{data?.body?.case_name}</span>
                  </div>

                  {data?.time && (
                    <div className="text-mutted small">
                      <span className="text-hint black-600 ms-0">{timeDifference(new Date(data?.time), new Date())} ago</span>
                    </div>
                  )}
                </div>
              )}

              {data?.body?.updated_at && (
                <div className="text-mutted small mx-4 justify-content-end">
                  <span className="text-hint black-600 ms-0">{timeDifference(new Date(data?.body?.updated_at), new Date())} ago</span>
                </div>
              )}
              {data?.body?.created_at && (
                <div className="text-mutted small mx-4 justify-content-end">
                  <span className="text-hint black-600 ms-0">{timeDifference(new Date(data?.body?.created_at), new Date())} ago</span>
                </div>
              )}
            </div>

            {!data.is_read && (
              <div className=" text-end align-items-center d-flex mark-read text-nowrap">
                <a
                  href="/"
                  onClick={(e) => markAsRead(e, data)}
                  className="flex-grow-1 text-end F-size12 text-mutted text-decoration-none ms-1"
                >
                  Mark as read
                </a>
              </div>
            )}
          </div>
        </div>
      );
    }
  };

  const notificationContent = () => {
    const { notification_type } = data || {};

    // Check if it's a generic notification type
    if (genericNotificationTypes.includes(notification_type)) {
      return renderGenericNotification();
    }

    if (notification_type === 'invitation') {
      return (
        <div className={data?.is_read ? 'p-2 notification-msg' : 'p-2 unread-notification rounded-0 border-0'}>
          <div className="d-flex justify-content-between">
            <div className="d-flex">
              <i className="fas fa-tasks mt-1 text-muted px-3"></i>
              <div className="d-flex">
                <div className="d-flex">
                  <div>
                    {data?.body?.event_name && (
                      <div className="mx-2">
                        <span className={data?.is_read ? '' : 'text-bold'}>
                          Invitation for the event <b>{data.body.event_name}</b>{' '}
                        </span>
                      </div>
                    )}

                    {data?.body?.event_time && (
                      <div className="mx-2">
                        <span className={data.is_read ? '' : 'text-bold'}>
                          {handleDateTimeOffset(
                            userDetails.timezone,
                            data?.body?.event_time,
                            constants.month_date_year_time_seconds_12_format
                          )}
                        </span>
                      </div>
                    )}

                    {data?.body?.case_name && (
                      <div className="mx-2">
                        <span>{data?.body?.case_name}</span>
                      </div>
                    )}

                    {data?.time && (
                      <div className="text-muted small mx-2">
                        <span className="text-hint black-600 ms-0">{timeDifference(new Date(data?.time), new Date())} ago</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {!data.is_read && (
              <div className=" text-end align-items-center d-flex mark-read text-nowrap">
                <a
                  href="/"
                  onClick={(e) => {
                    e.preventDefault();
                    markAsRead(e, data);
                  }}
                  className="flex-grow-1 text-end F-size12 text-mutted text-decoration-none ms-1"
                >
                  Mark as read
                </a>
              </div>
            )}
          </div>
        </div>
      );
    } else if (notification_type === 'reminder') {
      return (
        <div className={data.is_read ? 'p-2' : 'unread-notification rounded-0 border-0'}>
          <div className="d-flex justify-content-between align-items-center">
            <div className="col-12 col-sm-9 d-flex align-items-center">
              <div className="d-flex align-items-center">
                <i className="far fa-calendar-alt text-muted px-3 mt-2 align-self-start"></i>
                <div className="">
                  {data?.body && (
                    <div className="mx-2">
                      <b> {data.body.message}</b>
                    </div>
                  )}
                  {data?.body && data?.body?.event_name && (
                    <div className="mx-2">
                      <b> {data.body.event_name}</b>
                    </div>
                  )}
                  {data?.body?.due_date && (
                    <div className="mx-2">
                      <span className={data.is_read ? '' : 'text-bold'}>
                        Due Date :{' '}
                        {handleDateTimeOffset(userDetails.timezone, data.body.due_date, constants.month_date_year_time_seconds_12_format)}
                      </span>
                    </div>
                  )}
                  {data?.body && data?.body?.event_time && (
                    <div className="mx-2">
                      {handleDateTimeOffset(userDetails?.timezone, data.body.event_time, constants.month_date_year_time_seconds_12_format)}
                    </div>
                  )}

                  <div className="d-flex flex-column">
                    {data?.body?.case_name && data?.body?.case_name !== '-' && <div className="ms-2">{data?.body?.case_name}</div>}

                    {data?.time && (
                      <div className="text-mutted small mx-2">
                        <span className="text-hint black-600 ms-0">{timeDifference(new Date(data?.time), new Date())} ago</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {!data.is_read && (
              <div className=" text-end align-items-center d-flex mark-read text-nowrap">
                <a
                  href="/"
                  onClick={(e) => {
                    e.preventDefault();
                    markAsRead(e, data);
                  }}
                  className="flex-grow-1 text-end F-size12 text-mutted text-decoration-none ms-1"
                >
                  Mark as read
                </a>
              </div>
            )}
          </div>
        </div>
      );
    } else if (notification_type === 'secure_message') {
      return (
        <div className={data.is_read ? 'p-2 notification-msg' : 'p-2 unread-notification rounded-0 border-0'}>
          <div className="d-flex justify-content-between">
            <div className="col-12 col-sm-9">
              <div className="d-flex">
                {data?.body?.created_by_name?.first_name && (
                  <Avatar
                    label={data?.body?.created_by_name?.first_name?.charAt(0)}
                    className="me-3 mt-1 ms-1"
                    size="normal"
                    style={{ backgroundColor: '#9c27b0', color: '#ffffff' }}
                    shape="circle"
                  />
                )}
                <span>
                  <span className="text-bold">{generateFullName(data.body?.created_by_name)}</span>
                  <span className="fw-normal"> sent a new message</span>
                  {data?.time && (
                    <div className="text-mutted small">
                      <span className="text-hint black-600 ms-0">{timeDifference(new Date(data?.time), new Date())} ago</span>
                    </div>
                  )}
                </span>
              </div>
            </div>
            {!data.is_read && (
              <div className=" text-end align-items-center d-flex mark-read text-nowrap">
                <a
                  href="/"
                  onClick={(e) => markAsRead(e, data)}
                  className="flex-grow-1 text-end F-size12 text-mutted text-decoration-none ms-1"
                >
                  Mark as read
                </a>
              </div>
            )}
          </div>
        </div>
      );
    } else if (notification_type === 'sidebar_message') {
      return (
        <div className={data.is_read ? 'p-2 notification-msg' : 'p-2 unread-notification rounded-0 border-0'}>
          <div className="d-flex justify-content-between">
            <div className="col-12 col-sm-9">
              <div className="d-flex">
                <div className="align-self-start">
                  <i className="fa fa-regular fa-bell text-muted px-3 mt-1 me-2"></i>
                </div>
                {/* <Avatar
                      label={data?.body?.created_by_name?.first_name?.charAt(0)}
                      className="me-1 mt-1 "
                      size="normal"
                      style={{ backgroundColor: '#9c27b0', color: '#ffffff' }}
                      shape="circle"
                    /> */}
                <span>
                  {data?.case_description?.case_description?.case_name && (
                    <span className="text-bold">Case: {`${data?.case_description?.case_description?.case_name}`}</span>
                  )}
                  <br />
                  <span className="fw-normal">New sidebar message received</span>
                  {data?.time && (
                    <div className="text-mutted small">
                      <span className="text-hint black-600 ms-0">{timeDifference(new Date(data?.time), new Date())} ago</span>
                    </div>
                  )}
                </span>
                {/* )} */}
              </div>
            </div>
            {!data.is_read && (
              <div className=" text-end align-items-center d-flex mark-read text-nowrap">
                <a
                  href="/"
                  onClick={(e) => markAsRead(e, data)}
                  className="flex-grow-1 text-end F-size12 text-mutted text-decoration-none ms-1"
                >
                  Mark as read
                </a>
              </div>
            )}
          </div>
        </div>
      );
    } else if (notification_type === 'assignment') {
      return (
        <div className={data?.is_read ? 'p-2 notification-msg' : 'p-2 unread-notification rounded-0 border-0'}>
          <div className="d-flex justify-content-between">
            <div className="d-flex">
              <i className="fas fa-tasks mt-1 text-muted p-3 pt-1"></i>
              <div className="d-flex">
                <div className="d-flex">
                  <div className="d-flex flex-wrap">
                    {data?.body?.task_name && (
                      <div className="mx-2">
                        <span className={data.is_read ? '' : 'text-bold'}>
                          A task has been assigned to you: <span className="text-bold">{data.body.task_name}</span>{' '}
                        </span>
                      </div>
                    )}

                    {data?.body?.due_date && (
                      <div className="mx-2">
                        <span className={data.is_read ? '' : 'text-bold'}>
                          Due Date :{' '}
                          {handleDateTimeOffset(userDetails.timezone, data.body.due_date, constants.month_date_year_time_seconds_12_format)}
                        </span>
                      </div>
                    )}
                    <div className="ms-2">
                      {/* TODO: "-" should be handled when creating task */}
                      {data?.body?.case_name && data?.body?.case_name !== '-' && <div>{data.body.case_name + ' Case'}</div>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {!data?.is_read && (
              <div className=" text-end align-items-center d-flex mark-read text-nowrap">
                <a
                  href="/"
                  onClick={(e) => markAsRead(e, data)}
                  className="flex-grow-1 text-end F-size12 text-mutted text-decoration-none ms-1"
                >
                  Mark as read
                </a>
              </div>
            )}
          </div>
        </div>
      );
    } else if (notification_type === 'automation_error_logs') {
      if (data?.body) {
        let { message, count } = data?.body || {};

        if (message && count) {
          message = `${message} (${count})`;
        }

        if (message) {
          return (
            <div className={data?.is_read ? 'p-0 notification-msg' : 'p-0 unread-notification rounded-0 border-0'}>
              <div className="d-flex justify-content-between">
                <div className="align-self-start">
                  <i className="fa fa-regular fa-bell text-muted px-3 mt-1 me-2"></i>
                </div>
                <div className="">{message || 'N/A'}</div>

                {!data.is_read && (
                  <div className=" text-end align-items-center d-flex mark-read text-nowrap">
                    <a
                      href="/"
                      onClick={(e) => markAsRead(e, data)}
                      className="flex-grow-1 text-end F-size12 text-mutted text-decoration-none ms-1"
                    >
                      Mark as read
                    </a>
                  </div>
                )}
              </div>
            </div>
          );
        }
      }
    } else if (notification_type === 'ai_summary_message') {
      return (
        <div className={data.is_read ? 'p-2 notification-msg' : 'p-2 unread-notification rounded-0 border-0'}>
          <div className="d-flex justify-content-between">
            <div className="col-12 col-sm-9 d-flex align-items-center">
              <div className="d-flex align-items-center">
                <i className="far fa-calendar-alt text-muted px-3 mt-2 align-self-start"></i>
                <div className="">
                  {data?.body && (
                    <div className="mx-2">
                      <b> {'AI File Summary'}</b>
                    </div>
                  )}
                  {data?.body && data?.body?.summary?.file_name && (
                    <div className="mx-2">
                      <b> {data.body.summary.file_name}</b>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {!data.is_read && (
              <div className=" text-end align-items-center d-flex mark-read text-nowrap">
                <a
                  href="/"
                  onClick={(e) => {
                    e.preventDefault();
                    markAsRead(e, data);
                  }}
                  className="flex-grow-1 text-end F-size12 text-mutted text-decoration-none ms-1"
                >
                  Mark as read
                </a>
              </div>
            )}
          </div>
        </div>
      );
    } else if (notification_type === 'incoming_email') {
      return (
        <div className={data.is_read ? 'p-2 notification-msg' : 'p-2 unread-notification rounded-0 border-0'}>
          <div className="d-flex justify-content-between">
            <div className="col-12 col-sm-9">
              <div className="d-flex">
                <Avatar
                  label={data?.body?.email_from?.user_email?.charAt(0)}
                  className="me-2 ms-2 mt-1 "
                  size="normal"
                  style={{ backgroundColor: '#9c27b0', color: '#ffffff' }}
                  shape="circle"
                />
                <span>
                  <span className="text-bold">{`${data?.body?.email_from?.user_name || data?.body?.email_from?.user_email}`}</span>
                  <span className="fw-normal"> sent a new email</span>
                  <div className="text-mutted small">
                    <span className="text-hint black-600 ms-0">
                      {timeDifference(new Date(data?.time || data?.body?.timestamp), new Date())} ago
                    </span>
                  </div>
                </span>
              </div>
            </div>
            {!data.is_read && (
              <div className=" text-end align-items-center d-flex mark-read text-nowrap">
                <a
                  href="/"
                  onClick={(e) => markAsRead(e, data)}
                  className="flex-grow-1 text-end F-size12 text-mutted text-decoration-none ms-1"
                >
                  Mark as read
                </a>
              </div>
            )}
          </div>
        </div>
      );
    } else if (notification_type === 'ai_entity_message') {
      return (
        <div className={data.is_read ? 'p-2 notification-msg' : 'p-2 unread-notification rounded-0 border-0'}>
          <div className="d-flex justify-content-between">
            <div className="col-12 col-sm-9 d-flex align-items-center">
              <div className="d-flex align-items-center">
                <i className="far fa-calendar-alt text-muted px-3 mt-2 align-self-start"></i>
                <div className="">
                  {data?.body && (
                    <div className="mx-2">
                      <b> {'AI Entity Detection'}</b>
                    </div>
                  )}
                  {data?.body?.entity?.case_name && (
                    <div className="mx-2">
                      Case: <span className="text-bold">{data?.body?.entity?.case_name}</span>
                    </div>
                  )}
                  {data?.body?.entity?.main_client_name && (
                    <div className="mx-2">
                      Lead: <span className="text-bold">{data?.body?.entity?.main_client_name}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {!data.is_read && (
              <div className=" text-end align-items-center d-flex mark-read text-nowrap">
                <a
                  href="/"
                  onClick={(e) => {
                    e.preventDefault();
                    markAsRead(e, data);
                  }}
                  className="flex-grow-1 text-end F-size12 text-mutted text-decoration-none ms-1"
                >
                  Mark as read
                </a>
              </div>
            )}
          </div>
        </div>
      );
    } else if (notification_type === 'ai_timeline_message') {
      return (
        <div className={data.is_read ? 'p-2 notification-msg' : 'p-2 unread-notification rounded-0 border-0'}>
          <div className="d-flex justify-content-between">
            <div className="col-12 col-sm-9 d-flex align-items-center">
              <div className="d-flex align-items-center">
                <i className="far fa-calendar-alt text-muted px-3 mt-2 align-self-start"></i>
                <div className="mx-2">
                  {data?.body && (
                    <div className="">
                      <b> {'AI Timeline'}</b>
                    </div>
                  )}
                  {data?.body?.timeline?.case_name && (
                    <div>
                      Case: <span className="text-bold">{data?.body?.timeline?.case_name}</span>
                    </div>
                  )}
                  {data?.body?.timeline?.main_client_name && (
                    <div>
                      Lead: <span className="text-bold">{data?.body?.timeline?.main_client_name}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {!data.is_read && (
              <div className=" text-end align-items-center d-flex mark-read text-nowrap">
                <a
                  href="/"
                  onClick={(e) => {
                    e.preventDefault();
                    markAsRead(e, data);
                  }}
                  className="flex-grow-1 text-end F-size12 text-mutted text-decoration-none ms-1"
                >
                  Mark as read
                </a>
              </div>
            )}
          </div>
        </div>
      );
    } else if (data?.body?.message) {
      return (
        <div className={data?.is_read ? 'notification-msg' : 'unread-notification rounded-0 border-0'}>
          <div className=" d-flex  align-items-center">
            <div className="align-self-start">
              {data?.body?.contact_name || data?.body?.client_name ? (
                <span className="ms-2">
                  <Avatar
                    label={data?.body?.contact_name?.charAt(0) || data?.body?.client_name?.charAt(0)}
                    className="me-1 mt-1 "
                    size="normal"
                    style={{
                      backgroundColor: '#9c27b0 !important',
                      color: '#fff !important',
                    }}
                    shape="circle"
                  />
                </span>
              ) : (
                <i className="fa fa-regular fa-bell text-muted px-3 mt-1"></i>
              )}
            </div>
            <div className="ms-2">
              <div className="d-flex">
                {data?.body?.contact_name && (
                  <span className="">
                    <span className="text-bold">{data?.body?.contact_name}</span>
                  </span>
                )}
              </div>

              {data?.body?.message && data?.body?.message === 'Inbound text message' ? (
                <div className="">
                  <span>
                    Text Message Received from <span className="text-bold">{data?.body?.client_name}</span>
                  </span>
                  {data?.time && <div className="text-hint black-600 ms-0">{timeDifference(new Date(data?.time), new Date())} ago</div>}
                </div>
              ) : (
                data?.body?.message
              )}

              <div className="d-flex justify-content-between">
                {data?.body?.case_name && (
                  <div>
                    <div>
                      Case: <span className="text-bold">{data?.body?.case_name}</span>
                    </div>

                    {data?.time && (
                      <div className="text-mutted small">
                        <span className="text-hint black-600 ms-0">{timeDifference(new Date(data?.time), new Date())} ago</span>
                      </div>
                    )}
                  </div>
                )}

                {data?.body?.updated_at && (
                  <div className="text-mutted small justify-content-end">
                    <span className="text-hint black-600 ms-0">{timeDifference(new Date(data?.body?.updated_at), new Date())} ago</span>
                  </div>
                )}
                {data?.body?.created_at && (
                  <div className="text-mutted small justify-content-end">
                    <span className="text-hint black-600 ms-0">{timeDifference(new Date(data?.body?.created_at), new Date())} ago</span>
                  </div>
                )}
              </div>
            </div>

            {!data.is_read && (
              <div className=" text-end align-items-center d-flex mark-read text-nowrap">
                <a
                  href="/"
                  onClick={(e) => markAsRead(e, data)}
                  className="flex-grow-1 text-end F-size12 text-mutted text-decoration-none ms-1"
                >
                  Mark as read
                </a>
              </div>
            )}
          </div>
        </div>
      );
    } else if (notification_type === 'message_viewed_by') {
      return (
        <div className={data?.is_read ? 'p-0 notification-msg' : 'p-0 unread-notification rounded-0 border-0'}>
          <div className="d-flex justify-content-between ms-2">
            <div className="align-self-start">
              <i className="fa fa-regular fa-bell text-muted px-3 mt-1"></i>
            </div>
            <div>{data?.body?.viewed_by_details?.viewed_by ? `Message viewed by ${data?.body?.viewed_by_details?.viewed_by}` : 'N/A'}</div>
            {!data.is_read && (
              <div className=" text-end align-items-center d-flex mark-read text-nowrap">
                <a
                  href="/"
                  onClick={(e) => markAsRead(e, data)}
                  className="flex-grow-1 text-end F-size12 text-mutted text-decoration-none ms-1"
                >
                  Mark as read
                </a>
              </div>
            )}
          </div>
        </div>
      );
    }
  };

  return <>{isClient ? notificationClientContent() : notificationContent()}</>;
};

export default NotificationContent;
